import { useState } from "react";
import PageHeader from "components/PageHeader";
import { DSBaseTable, DSTableCell } from "../../system-design/Table";
import { Column } from "../../system-design/Table/DSBaseTable/components/DSBaseTable";
import DSBox from "../../system-design/DSBox";
import DSCard from "../../system-design/DSCard";
import DSCardContent from "../../system-design/DSCardContent";
import DSTypography from "../../system-design/DSTypography";
import DSTextField from "../../system-design/DSTextField";
import DSPrimaryButton from "../../system-design/DSPrimaryButton";
import DSButton from "../../system-design/DSButton";
import DSChip from "../../system-design/DSChip";
import serialsClient, {
  SerialBatchFile,
  batchFileName,
} from "../../../api/serials";
import useSerialsBatches from "../hooks/useSerialsBatches";
import useSerialLookup from "../hooks/useSerialLookup";
import useGenerationStatus from "../hooks/useGenerationStatus";
import GenerateBatchModal from "./GenerateBatchModal";
import TestSerialsCard from "./TestSerialsCard";

const formatBytes = (bytes: number) => {
  if (!bytes) return "-";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const formatDate = (iso: string) => {
  if (!iso) return "-";
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? iso : date.toLocaleString();
};

const Field = ({ label, value }: { label: string; value: string | null }) => (
  <DSBox>
    <DSTypography variant="caption" color="textSecondary">
      {label}
    </DSTypography>
    <DSTypography variant="body2">{value || "—"}</DSTypography>
  </DSBox>
);

function Serials() {
  const [showGenerate, setShowGenerate] = useState(false);
  const [lookupInput, setLookupInput] = useState("");
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const { data: batches } = useSerialsBatches();
  const { data: generation } = useGenerationStatus();
  const lookup = useSerialLookup();

  const batchColumns: Column<SerialBatchFile>[] = [
    { label: "Batch file", field: "name" },
    { label: "Size", field: "sizeBytes" },
    { label: "Updated", field: "updatedAt" },
    { label: "Download", field: "name" },
  ];

  const batchRow = (
    batch: SerialBatchFile,
    columns: Column<SerialBatchFile>[],
  ) => (
    <>
      {columns.map((col, index) => {
        const key = `${col.label}-${index}`;
        if (col.label === "Size") {
          return (
            <DSTableCell key={key}>{formatBytes(batch.sizeBytes)}</DSTableCell>
          );
        }
        if (col.label === "Updated") {
          return (
            <DSTableCell key={key}>{formatDate(batch.updatedAt)}</DSTableCell>
          );
        }
        if (col.label === "Download") {
          return (
            <DSTableCell key={key}>
              <DSButton
                size="small"
                onClick={async (e) => {
                  e.stopPropagation();
                  setDownloadError(null);
                  try {
                    await serialsClient.downloadBatch(batch.name);
                  } catch {
                    setDownloadError(
                      `Failed to download ${batchFileName(batch.name)}`,
                    );
                  }
                }}
              >
                Download
              </DSButton>
            </DSTableCell>
          );
        }
        return <DSTableCell key={key}>{batchFileName(batch.name)}</DSTableCell>;
      })}
    </>
  );

  const result = lookup.data;

  return (
    <>
      <PageHeader
        overline="Devices"
        title="Serials"
        subtitle="Generate serial batches and look up a clock by serial / friendly identifier / clock ID."
        actions={
          <DSBox sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            {generation?.running && (
              <DSChip
                size="small"
                color="warning"
                label={`Generation running since ${formatDate(
                  generation.startedAt || "",
                )}`}
              />
            )}
            <DSPrimaryButton
              onClick={() => setShowGenerate(true)}
              disabled={generation?.running}
            >
              Generate batch
            </DSPrimaryButton>
          </DSBox>
        }
      />

      {/* Lookup */}
      <DSCard sx={{ mb: 3 }}>
        <DSCardContent>
          <DSTypography variant="h6" sx={{ mb: 1.5 }}>
            Lookup
          </DSTypography>
          <DSBox sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
            <DSTextField
              value={lookupInput}
              onChange={(e) => setLookupInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && lookupInput.trim()) {
                  lookup.mutate(lookupInput.trim());
                }
              }}
              placeholder="Serial, friendly identifier, or clock ID…"
              sx={{ flex: 1, minWidth: 280 }}
            />
            <DSPrimaryButton
              onClick={() => lookup.mutate(lookupInput.trim())}
              disabled={!lookupInput.trim() || lookup.isPending}
            >
              {lookup.isPending ? "Looking up…" : "Lookup"}
            </DSPrimaryButton>
          </DSBox>

          {lookup.isError && (
            <DSTypography variant="body2" color="error" sx={{ mt: 2 }}>
              {(lookup.error as Error)?.message || "Not found"}
            </DSTypography>
          )}

          {result && (
            <DSBox
              sx={{
                mt: 2,
                display: "grid",
                gridTemplateColumns: { xxs: "1fr", sm: "1fr 1fr" },
                gap: 1,
              }}
            >
              <Field label="Serial" value={result.serialNumber} />
              <Field
                label="Friendly identifier"
                value={result.friendlyIdentifier}
              />
              <Field label="Clock ID" value={result.clockId} />
              <Field label="Auth email" value={result.email} />
              <Field
                label="Auth created"
                value={formatDate(result.authCreatedAt || "")}
              />
              <Field
                label="Version"
                value={result.version?.toString() ?? null}
              />
              <DSBox>
                <DSTypography variant="caption" color="textSecondary">
                  Registered
                </DSTypography>
                <DSBox sx={{ mt: 0.5 }}>
                  <DSChip
                    size="small"
                    color={result.registered ? "success" : "default"}
                    label={result.registered ? "Registered" : "Not registered"}
                  />
                </DSBox>
              </DSBox>
              <Field
                label="Registration email"
                value={result.registrationEmail}
              />
              <Field
                label="Registration date"
                value={
                  result.registrationDate
                    ? formatDate(result.registrationDate)
                    : null
                }
              />
              <Field
                label="Orders"
                value={result.orders.length ? result.orders.join(", ") : "—"}
              />
              <Field label="Gifts" value={result.giftCount.toString()} />
            </DSBox>
          )}
        </DSCardContent>
      </DSCard>

      <TestSerialsCard />

      {/* Batches */}
      <DSCard>
        <DSCardContent>
          <DSTypography variant="h6" sx={{ mb: 1.5 }}>
            Batches
          </DSTypography>
          {downloadError && (
            <DSTypography variant="body2" color="error" sx={{ mb: 1.5 }}>
              {downloadError}
            </DSTypography>
          )}
          {batches && batches.length > 0 ? (
            <DSBaseTable
              data={batches}
              columns={batchColumns}
              rowBuilder={batchRow}
            />
          ) : (
            <DSTypography variant="body2" color="textSecondary">
              No batches found.
            </DSTypography>
          )}
        </DSCardContent>
      </DSCard>

      <GenerateBatchModal
        show={showGenerate}
        onClose={() => setShowGenerate(false)}
      />
    </>
  );
}

export default Serials;
